import {Map as GoogleMap} from '@vis.gl/react-google-maps';
import {motion} from 'framer-motion';
import {Button} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import styled from 'styled-components';

import {equals, isNil, last} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useMapIntro} from '../hooks/useMapIntro';
import {useRouteAnimation} from '../hooks/useRouteAnimation';
import {getDrawnPath} from '../utils/getDrawnPath';
import {getRouteBounds, RoutePoint} from '../utils/getRouteBounds';
import {routeMapStyles} from '../utils/routeMapStyles';
import {RouteLine} from './RouteLine';
import {RouteMarkers} from './RouteMarkers';
import {RouteSummary} from './RouteSummary';

// The map fills the height of the attributes beside it, but never gets smaller than this.
const MAP_MIN_HEIGHT = 320;
// Share of the map that has to be on screen before the route starts drawing.
const VIEWPORT_VISIBLE_AMOUNT = 0.5;
const MAP_PADDING = 48;
// Street level, so a single place still shows its surroundings instead of the maximum zoom.
const SINGLE_PLACE_ZOOM = 16;
const MAP_FADE_IN_MS = 300;

const MapFrame = styled(motion.div)`
  position: relative;
  height: 100%;
  min-height: ${MAP_MIN_HEIGHT}px;
  border-radius: ${({theme}) => theme.radii.large};
  box-shadow: ${({theme}) => theme.shadows.elevation_1};
  overflow: hidden;
`;

type AnimatedRouteMapProps = TestIdProps & {
  /** The smoothed route. */
  path: RoutePoint[];
  /** Distance in metres from the start to every point of `path`. */
  distances: number[];
  /** Distance and duration of the drive, shown over the map. */
  summary?: string | Nullish;
};

export function AnimatedRouteMap(props: AnimatedRouteMapProps) {
  // A drive without movement records a single place, which a line cannot show, so it gets a marker.
  const singlePlace = props.path.every((point) => equals(point, props.path[0]))
    ? props.path[0]
    : null;
  const totalDistance = last(props.distances) ?? 0;
  const animation = useRouteAnimation(totalDistance);
  const bounds = getRouteBounds(props.path);
  const hasCameraIntro = isNil(singlePlace) && !animation.shouldReduceMotion;

  const intro = useMapIntro(hasCameraIntro, animation.start);

  const drawnPath = isNil(animation.drawnDistance)
    ? []
    : getDrawnPath(props.path, props.distances, animation.drawnDistance);

  return (
    // The route is drawn once the map is really on screen, so the user sees it being drawn.
    <MapFrame
      viewport={{once: true, amount: VIEWPORT_VISIBLE_AMOUNT}}
      onViewportEnter={intro.handleViewportEnter}
      data-testid={props['data-testid']}
    >
      <Show when={bounds}>
        {(routeBounds) => (
          <Box
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            left={0}
            opacity={intro.isMapHidden ? 0 : 1}
            transition={`opacity ${MAP_FADE_IN_MS}ms ease-out`}
          >
            <GoogleMap
              {...(singlePlace
                ? {defaultCenter: singlePlace, defaultZoom: SINGLE_PLACE_ZOOM}
                : {defaultBounds: {...routeBounds, padding: MAP_PADDING}})}
              styles={routeMapStyles}
              gestureHandling="cooperative"
              disableDefaultUI
              fullscreenControl
              clickableIcons={false}
              onIdle={intro.handleIdle}
            >
              <Show when={isNil(singlePlace)}>
                <RouteLine pointCount={props.path.length} drawnPath={drawnPath} />
              </Show>
              <RouteMarkers
                path={props.path}
                drawnPath={drawnPath}
                isFinished={animation.isFinished}
              />
            </GoogleMap>
          </Box>
        )}
      </Show>
      <Show when={isNotNilOrEmpty(props.summary) ? props.summary : null}>
        {(summary) => <RouteSummary summary={summary} data-testid={props['data-testid']} />}
      </Show>
      <Show when={animation.canReplay}>
        <Box position="absolute" left={3} bottom={8} borderRadius="medium" boxShadow="elevation_2">
          <Button
            variant="secondary"
            leftIcon="action/replay"
            title={i18n.t('entity.condition.actions.replayTestDriveRoute')}
            onClick={animation.start}
            data-testid={suffixTestId('replay', props)}
          />
        </Box>
      </Show>
    </MapFrame>
  );
}
